import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import axios from 'axios';
import CreateEventButton from '../components/CreateEventButton';
import {
  Building2, Plus, Edit, Trash2, Loader2, Users, DollarSign,
  Calendar, Package, MapPin, CheckCircle2, X, Clock,
  ArrowUpRight, Eye, Star, Armchair, Monitor, Sparkles,
  Ban, ChevronLeft, ChevronRight, Link2, Unlink2
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SETUP_STYLES = ['Theater', 'Banquet', 'Cocktail', 'Classroom', 'U-Shape', 'Boardroom', 'Hollow Square'];
const ADDON_CATS = [
  { id: 'furniture', name: 'Furniture' }, { id: 'av_equipment', name: 'A/V Equipment' },
  { id: 'decor', name: 'Decor & Linens' }, { id: 'setup', name: 'Setup & Cleanup' },
  { id: 'staffing', name: 'On-site Staff' }, { id: 'other', name: 'Other' },
];

export default function VenueOwnerDashboard() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [allProducts, setAllProducts] = useState([]);

  // Dialog states
  const [showSpaceDialog, setShowSpaceDialog] = useState(false);
  const [showAddonDialog, setShowAddonDialog] = useState(false);
  const [showPackageDialog, setShowPackageDialog] = useState(false);
  const [editingSpace, setEditingSpace] = useState(null);
  const [editingAddon, setEditingAddon] = useState(null);
  const [editingPackage, setEditingPackage] = useState(null);
  const [saving, setSaving] = useState(false);

  // Forms
  const [spaceForm, setSpaceForm] = useState({ name: '', description: '', capacity_min: 10, capacity_max: 100, hourly_rate: 0, half_day_rate: 0, full_day_rate: 0, minimum_hours: 2, amenities: [], setup_styles: [], photos: [] });
  const [addonForm, setAddonForm] = useState({ name: '', category: 'furniture', description: '', pricing_type: 'per_unit', price: 0, max_quantity: 50, photo: '' });
  const [pkgForm, setPkgForm] = useState({ name: '', description: '', cover_photo: '', space_id: '', duration_hours: 8, addon_selections: [], service_ids: [], package_price: 0, capacity_min: 20, capacity_max: 200, tags: '', is_featured: false });

  // Calendar & Availability
  const [calMonth, setCalMonth] = useState(() => { const n = new Date(); return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}`; });
  const [monthDays, setMonthDays] = useState([]);
  const [calLoading, setCalLoading] = useState(false);
  const [googleCalConnected, setGoogleCalConnected] = useState(false);
  const [googleEvents, setGoogleEvents] = useState([]);
  const [blockedDates, setBlockedDates] = useState([]);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = useCallback(async () => {
    try {
      const [venueRes, productsRes] = await Promise.all([
        axios.get(`${API}/venues/my-venue`, { headers }),
        axios.get(`${API}/services/products`, { headers }),
      ]);
      setData(venueRes.data);
      setAllProducts(productsRes.data.products || []);
    } catch (err) {
      if (err.response?.status === 403) navigate('/venue-onboarding');
    } finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Calendar data fetch
  const fetchCalendar = useCallback(async () => {
    if (!data?.venue) return;
    setCalLoading(true);
    try {
      const [monthRes, calStatusRes, gEventsRes] = await Promise.all([
        axios.get(`${API}/venues/${data.venue.venue_id}/monthly-availability?month=${calMonth}`),
        axios.get(`${API}/venues/calendar/status`, { headers }).catch(() => ({ data: { connected: false } })),
        axios.get(`${API}/venues/calendar/google-events?month=${calMonth}`, { headers }).catch(() => ({ data: { events: [] } })),
      ]);
      setMonthDays(monthRes.data.days || []);
      setBlockedDates(monthRes.data.blocked_dates || []);
      setGoogleCalConnected(calStatusRes.data.connected || false);
      setGoogleEvents(gEventsRes.data.events || []);
    } catch (err) { console.error('Calendar fetch error:', err); }
    finally { setCalLoading(false); }
  }, [data?.venue, calMonth, token]);

  useEffect(() => { if (data?.venue) fetchCalendar(); }, [fetchCalendar]);

  if (loading) return <div className="min-h-screen bg-background flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-indigo-400" /></div>;

  const venue = data?.venue;
  if (!venue) {
    navigate('/venue-onboarding');
    return null;
  }

  const { spaces = [], addons = [], packages = [], bookings = [] } = data;
  const confirmed = bookings.filter(b => ['confirmed', 'deposit_paid', 'paid', 'completed'].includes(b.status));
  const totalRevenue = confirmed.reduce((s, b) => s + (b.venue_payout || 0), 0);
  const pendingBookings = bookings.filter(b => b.status === 'pending');

  // ===== SPACE HANDLERS =====
  const openSpaceDialog = (space = null) => {
    if (space) {
      setEditingSpace(space);
      setSpaceForm({ ...space, photos: space.photos || [] });
    } else {
      setEditingSpace(null);
      setSpaceForm({ name: '', description: '', capacity_min: 10, capacity_max: 100, hourly_rate: 0, half_day_rate: 0, full_day_rate: 0, minimum_hours: 2, amenities: [], setup_styles: [], photos: [] });
    }
    setShowSpaceDialog(true);
  };

  const saveSpace = async () => {
    if (!spaceForm.name) { toast.error('Space name required'); return; }
    setSaving(true);
    try {
      if (editingSpace) {
        await axios.put(`${API}/venues/${venue.venue_id}/spaces/${editingSpace.space_id}`, spaceForm, { headers });
        toast.success('Space updated');
      } else {
        await axios.post(`${API}/venues/${venue.venue_id}/spaces`, spaceForm, { headers });
        toast.success('Space added');
      }
      setShowSpaceDialog(false); fetchData();
    } catch (err) { toast.error('Failed to save space'); } finally { setSaving(false); }
  };

  const deleteSpace = async (spaceId) => {
    try { await axios.delete(`${API}/venues/${venue.venue_id}/spaces/${spaceId}`, { headers }); toast.success('Space removed'); fetchData(); }
    catch { toast.error('Failed'); }
  };

  // ===== ADDON HANDLERS =====
  const openAddonDialog = (addon = null) => {
    if (addon) { setEditingAddon(addon); setAddonForm({ ...addon }); }
    else { setEditingAddon(null); setAddonForm({ name: '', category: 'furniture', description: '', pricing_type: 'per_unit', price: 0, max_quantity: 50, photo: '' }); }
    setShowAddonDialog(true);
  };

  const saveAddon = async () => {
    if (!addonForm.name) { toast.error('Name required'); return; }
    setSaving(true);
    try {
      if (editingAddon) {
        await axios.put(`${API}/venues/${venue.venue_id}/addons/${editingAddon.addon_id}`, addonForm, { headers });
      } else {
        await axios.post(`${API}/venues/${venue.venue_id}/addons`, addonForm, { headers });
      }
      toast.success('Add-on saved'); setShowAddonDialog(false); fetchData();
    } catch (err) { toast.error('Failed'); } finally { setSaving(false); }
  };

  const deleteAddon = async (addonId) => {
    try { await axios.delete(`${API}/venues/${venue.venue_id}/addons/${addonId}`, { headers }); toast.success('Removed'); fetchData(); }
    catch { toast.error('Failed'); }
  };

  // ===== PACKAGE HANDLERS =====
  const openPackageDialog = (pkg = null) => {
    if (pkg) {
      setEditingPackage(pkg);
      setPkgForm({
        name: pkg.name, description: pkg.description, cover_photo: pkg.cover_photo || '',
        space_id: pkg.space?.space_id || '', duration_hours: pkg.space?.duration_hours || 8,
        addon_selections: pkg.addons?.map(a => ({ addon_id: a.addon_id, quantity: a.quantity })) || [],
        service_ids: pkg.services?.map(s => s.service_id) || [],
        package_price: pkg.package_price || 0,
        capacity_min: pkg.capacity_min || 20, capacity_max: pkg.capacity_max || 200,
        tags: (pkg.tags || []).join(', '), is_featured: pkg.is_featured || false,
      });
    } else {
      setEditingPackage(null);
      setPkgForm({ name: '', description: '', cover_photo: '', space_id: spaces[0]?.space_id || '', duration_hours: 8, addon_selections: [], service_ids: [], package_price: 0, capacity_min: 20, capacity_max: 200, tags: '', is_featured: false });
    }
    setShowPackageDialog(true);
  };

  const togglePkgAddon = (addonId) => {
    setPkgForm(p => {
      const exists = p.addon_selections.find(a => a.addon_id === addonId);
      return { ...p, addon_selections: exists ? p.addon_selections.filter(a => a.addon_id !== addonId) : [...p.addon_selections, { addon_id: addonId, quantity: 1 }] };
    });
  };

  const setPkgAddonQty = (addonId, qty) => {
    setPkgForm(p => ({
      ...p, addon_selections: p.addon_selections.map(a => a.addon_id === addonId ? { ...a, quantity: parseInt(qty) || 1 } : a)
    }));
  };

  const togglePkgService = (svcId) => {
    setPkgForm(p => ({
      ...p, service_ids: p.service_ids.includes(svcId) ? p.service_ids.filter(s => s !== svcId) : [...p.service_ids, svcId]
    }));
  };

  const savePackage = async () => {
    if (!pkgForm.name || !pkgForm.space_id) { toast.error('Name and space required'); return; }
    setSaving(true);
    try {
      const payload = { ...pkgForm, tags: pkgForm.tags ? pkgForm.tags.split(',').map(t => t.trim()) : [] };
      if (editingPackage) {
        await axios.put(`${API}/venues/${venue.venue_id}/packages/${editingPackage.package_id}`, payload, { headers });
      } else {
        await axios.post(`${API}/venues/${venue.venue_id}/packages`, payload, { headers });
      }
      toast.success('Package saved'); setShowPackageDialog(false); fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); } finally { setSaving(false); }
  };

  const deletePackage = async (pkgId) => {
    try { await axios.delete(`${API}/venues/${venue.venue_id}/packages/${pkgId}`, { headers }); toast.success('Deleted'); fetchData(); }
    catch { toast.error('Failed'); }
  };

  // ===== PREFERRED VENDORS =====
  const preferredIds = venue.preferred_vendor_ids || [];
  const togglePreferred = async (svcId) => {
    const newIds = preferredIds.includes(svcId) ? preferredIds.filter(s => s !== svcId) : [...preferredIds, svcId];
    try {
      await axios.put(`${API}/venues/${venue.venue_id}/preferred-vendors`, { service_ids: newIds }, { headers });
      toast.success('Vendor list updated');
      fetchData();
    } catch { toast.error('Failed'); }
  };

  // ===== BOOKING ACTIONS =====
  const updateBooking = async (bookingId, status) => {
    try {
      await axios.put(`${API}/venues/bookings/${bookingId}/status`, { status }, { headers });
      toast.success(`Booking ${status}`);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  // ===== CALENDAR HANDLERS =====
  const changeMonth = (delta) => {
    const [y, m] = calMonth.split('-').map(Number);
    const d = new Date(y, m - 1 + delta, 1);
    setCalMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  };

  const toggleBlockDate = async (dateStr) => {
    const newBlocked = blockedDates.includes(dateStr)
      ? blockedDates.filter(d => d !== dateStr)
      : [...blockedDates, dateStr];
    try {
      await axios.put(`${API}/venues/${venue.venue_id}/availability`, {
        operating_hours: venue.operating_hours || {},
        blocked_dates: newBlocked,
        minimum_notice_hours: venue.minimum_notice_hours || 24,
      }, { headers });
      setBlockedDates(newBlocked);
      toast.success(blockedDates.includes(dateStr) ? 'Date unblocked' : 'Date blocked');
      fetchCalendar();
    } catch { toast.error('Failed to update'); }
  };

  const connectGoogleCal = async () => {
    try {
      const res = await axios.post(`${API}/venues/calendar/connect`, {}, { headers });
      if (res.data.auth_url) window.location.href = res.data.auth_url;
    } catch (err) { toast.error('Failed to start Google Calendar connection'); }
  };

  const disconnectGoogleCal = async () => {
    try {
      await axios.post(`${API}/venues/calendar/disconnect`, {}, { headers });
      setGoogleCalConnected(false);
      setGoogleEvents([]);
      toast.success('Google Calendar disconnected');
    } catch { toast.error('Failed'); }
  };

  const calMonthLabel = (() => {
    const [y, m] = calMonth.split('-').map(Number);
    return new Date(y, m - 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  })();

  const getStatusColor = (status) => {
    switch (status) {
      case 'available': return 'bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30';
      case 'partially_booked': return 'bg-amber-500/20 text-amber-400';
      case 'fully_booked': return 'bg-red-500/20 text-red-400';
      case 'blocked': return 'bg-slate-600/40 text-muted-foreground';
      case 'closed': return 'bg-muted/40 text-muted-foreground';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  return (
    <div className="min-h-screen bg-background pt-20" data-testid="venue-dashboard">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="flex items-center gap-2">
              <Building2 className="h-5 w-5 text-indigo-400" />
              <h1 className="font-heading text-xl sm:text-2xl font-bold text-foreground" data-testid="venue-name">{venue.name}</h1>
              <Badge className={`text-[9px] ${
                venue.status === 'active' ? 'bg-emerald-500/20 text-emerald-400' :
                venue.status === 'pending_review' ? 'bg-amber-500/20 text-amber-400' :
                venue.status === 'rejected' ? 'bg-red-500/20 text-red-400' :
                'bg-muted text-muted-foreground'
              }`}>{venue.status === 'pending_review' ? 'Under Review' : venue.status}</Badge>
            </div>
            <p className="text-muted-foreground text-xs mt-0.5 flex items-center gap-1"><MapPin className="h-3 w-3" />{venue.address}, {venue.city}, {venue.state}</p>
          </div>
          <div className="flex items-center gap-2">
            <CreateEventButton />
            <Button size="sm" variant="outline" onClick={() => window.open(`/venues/${venue.slug}`, '_blank')}>
              <Eye className="h-3.5 w-3.5 mr-1" /> View Public Page
            </Button>
          </div>
        </div>

        {/* Pending Review Banner */}
        {venue.status === 'pending_review' && (
          <div className="mb-6 p-4 rounded-xl bg-amber-500/10 border border-amber-500/20 flex items-start gap-3" data-testid="pending-review-banner">
            <Clock className="h-5 w-5 text-amber-400 mt-0.5 shrink-0" />
            <div>
              <p className="text-sm font-semibold text-amber-300">Listing Under Review</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                Your venue listing is being reviewed by the Eventism team. You'll receive a notification once it's approved and live on the marketplace. In the meantime, feel free to add spaces, packages, and set up your calendar.
              </p>
            </div>
          </div>
        )}
        {venue.status === 'rejected' && (
          <div className="mb-6 p-4 rounded-xl bg-red-500/10 border border-red-500/20 flex items-start gap-3" data-testid="rejected-banner">
            <Ban className="h-5 w-5 text-red-400 mt-0.5 shrink-0" />
            <div>
              <p className="text-sm font-semibold text-red-300">Listing Needs Changes</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                {venue.rejection_reason || 'Your listing needs adjustments before it can go live. Please update your venue details and resubmit.'}
              </p>
            </div>
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="venue-stats">
          <Card className="bg-muted/60 border-border"><CardContent className="p-3">
            <p className="text-[10px] text-muted-foreground uppercase">Spaces</p>
            <p className="text-xl font-bold text-foreground">{spaces.length}</p>
          </CardContent></Card>
          <Card className="bg-muted/60 border-border"><CardContent className="p-3">
            <p className="text-[10px] text-muted-foreground uppercase">Packages</p>
            <p className="text-xl font-bold text-foreground">{packages.length}</p>
          </CardContent></Card>
          <Card className="bg-muted/60 border-border"><CardContent className="p-3">
            <p className="text-[10px] text-muted-foreground uppercase">Bookings</p>
            <p className="text-xl font-bold text-foreground">{venue.total_bookings || 0}</p>
          </CardContent></Card>
          <Card className="bg-muted/60 border-border"><CardContent className="p-3">
            <p className="text-[10px] text-muted-foreground uppercase">Revenue</p>
            <p className="text-xl font-bold text-emerald-400">${totalRevenue.toLocaleString()}</p>
          </CardContent></Card>
        </div>

        {/* TABS */}
        <Tabs defaultValue="spaces" className="space-y-4">
          <TabsList className="bg-card border border-border">
            <TabsTrigger value="spaces">Spaces</TabsTrigger>
            <TabsTrigger value="addons">Add-ons</TabsTrigger>
            <TabsTrigger value="packages">Packages</TabsTrigger>
            <TabsTrigger value="calendar">Calendar</TabsTrigger>
            <TabsTrigger value="vendors">Preferred Vendors</TabsTrigger>
            <TabsTrigger value="bookings">Bookings</TabsTrigger>
          </TabsList>

          {/* === SPACES TAB === */}
          <TabsContent value="spaces">
            <div className="flex justify-between items-center mb-3">
              <h2 className="text-sm font-semibold text-foreground">Spaces & Rooms</h2>
              <Button size="sm" onClick={() => openSpaceDialog()} className="bg-indigo-600 hover:bg-indigo-700" data-testid="add-space-btn"><Plus className="h-3.5 w-3.5 mr-1" /> Add Space</Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {spaces.map(s => (
                <Card key={s.space_id} className="bg-muted/60 border-border">
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="text-sm font-bold text-foreground">{s.name}</h3>
                        <p className="text-xs text-muted-foreground mt-0.5">{s.description || 'No description'}</p>
                        <div className="flex items-center gap-3 mt-2 text-[10px] text-muted-foreground">
                          <span className="flex items-center gap-0.5"><Users className="h-3 w-3" /> {s.capacity_min}-{s.capacity_max}</span>
                          {s.hourly_rate > 0 && <span className="flex items-center gap-0.5"><Clock className="h-3 w-3" /> ${s.hourly_rate}/hr</span>}
                          {s.full_day_rate > 0 && <span className="flex items-center gap-0.5"><Calendar className="h-3 w-3" /> ${s.full_day_rate}/day</span>}
                        </div>
                        {s.setup_styles?.length > 0 && <div className="flex gap-1 mt-1.5">{s.setup_styles.map(st => <Badge key={st} className="text-[8px] bg-muted text-muted-foreground">{st}</Badge>)}</div>}
                      </div>
                      <div className="flex gap-1">
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => openSpaceDialog(s)}><Edit className="h-3 w-3" /></Button>
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-400" onClick={() => deleteSpace(s.space_id)}><Trash2 className="h-3 w-3" /></Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
              {spaces.length === 0 && <p className="text-sm text-muted-foreground col-span-2 py-8 text-center">No spaces yet. Add your first space to start accepting bookings.</p>}
            </div>
          </TabsContent>

          {/* === ADD-ONS TAB === */}
          <TabsContent value="addons">
            <div className="flex justify-between items-center mb-3">
              <h2 className="text-sm font-semibold text-foreground">Rental Add-ons</h2>
              <Button size="sm" onClick={() => openAddonDialog()} className="bg-indigo-600 hover:bg-indigo-700" data-testid="add-addon-btn"><Plus className="h-3.5 w-3.5 mr-1" /> Add Item</Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              {addons.map(a => (
                <Card key={a.addon_id} className="bg-muted/60 border-border">
                  <CardContent className="p-3 flex items-center justify-between">
                    <div>
                      <p className="text-xs font-semibold text-foreground">{a.name}</p>
                      <p className="text-[10px] text-muted-foreground">{a.category} | ${a.price} {a.pricing_type === 'per_unit' ? '/unit' : 'flat'}</p>
                    </div>
                    <div className="flex gap-1">
                      <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => openAddonDialog(a)}><Edit className="h-3 w-3" /></Button>
                      <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-red-400" onClick={() => deleteAddon(a.addon_id)}><Trash2 className="h-3 w-3" /></Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
              {addons.length === 0 && <p className="text-sm text-muted-foreground col-span-3 py-8 text-center">No add-ons. Add chairs, tables, AV equipment, etc.</p>}
            </div>
          </TabsContent>

          {/* === PACKAGES TAB (ONE-CLICK EXPERIENCES) === */}
          <TabsContent value="packages">
            <div className="flex justify-between items-center mb-3">
              <div>
                <h2 className="text-sm font-semibold text-foreground flex items-center gap-1.5"><Sparkles className="h-4 w-4 text-amber-400" /> One-Click Packages</h2>
                <p className="text-[10px] text-muted-foreground">Bundle a space + add-ons + vendor services into bookable experiences</p>
              </div>
              <Button size="sm" onClick={() => openPackageDialog()} className="bg-indigo-600 hover:bg-indigo-700" data-testid="add-package-btn"><Plus className="h-3.5 w-3.5 mr-1" /> New Package</Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {packages.map(pkg => (
                <Card key={pkg.package_id} className="bg-muted/60 border-border overflow-hidden">
                  {pkg.cover_photo && <img src={pkg.cover_photo} alt="" className="w-full h-32 object-cover" />}
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="text-sm font-bold text-foreground">{pkg.name}</h3>
                          {pkg.is_featured && <Badge className="bg-amber-500/20 text-amber-400 text-[8px]">Featured</Badge>}
                        </div>
                        <p className="text-xs text-muted-foreground mt-0.5">{pkg.description || 'No description'}</p>
                        <div className="flex items-center gap-3 mt-2 text-[10px]">
                          <span className="text-muted-foreground">{pkg.space?.name} | {pkg.space?.duration_hours}hrs</span>
                          {pkg.addons?.length > 0 && <span className="text-muted-foreground">{pkg.addons.length} add-ons</span>}
                          {pkg.services?.length > 0 && <span className="text-indigo-400">{pkg.services.length} services</span>}
                        </div>
                        <div className="flex items-center gap-2 mt-2">
                          <span className="text-lg font-bold text-emerald-400">${pkg.package_price?.toLocaleString()}</span>
                          {pkg.savings > 0 && <span className="text-[10px] text-lime-400">Save ${pkg.savings.toLocaleString()}</span>}
                        </div>
                      </div>
                      <div className="flex gap-1">
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => openPackageDialog(pkg)}><Edit className="h-3 w-3" /></Button>
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-400" onClick={() => deletePackage(pkg.package_id)}><Trash2 className="h-3 w-3" /></Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
              {packages.length === 0 && (
                <div className="col-span-2 text-center py-12">
                  <Package className="h-10 w-10 text-muted-foreground mx-auto mb-2" />
                  <h3 className="text-sm font-semibold text-foreground">No packages yet</h3>
                  <p className="text-xs text-muted-foreground mt-0.5">Create one-click experiences that bundle your space, add-ons, and preferred vendor services</p>
                </div>
              )}
            </div>
          </TabsContent>

          {/* === CALENDAR & AVAILABILITY TAB === */}
          <TabsContent value="calendar">
            <div className="space-y-4">
              {/* Google Calendar Sync */}
              <Card className="bg-muted/60 border-border">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-indigo-400" />
                      <div>
                        <h3 className="text-sm font-semibold text-foreground">Google Calendar Sync</h3>
                        <p className="text-[10px] text-muted-foreground">Auto-sync bookings and availability with Google Calendar</p>
                      </div>
                    </div>
                    {googleCalConnected ? (
                      <div className="flex items-center gap-2">
                        <Badge className="bg-emerald-500/20 text-emerald-400 text-[9px]"><CheckCircle2 className="h-3 w-3 mr-0.5" /> Connected</Badge>
                        <Button size="sm" variant="outline" onClick={disconnectGoogleCal} className="text-red-400 text-[10px] h-7" data-testid="disconnect-google-cal">
                          <Unlink2 className="h-3 w-3 mr-0.5" /> Disconnect
                        </Button>
                      </div>
                    ) : (
                      <Button size="sm" onClick={connectGoogleCal} className="bg-indigo-600 hover:bg-indigo-700 text-xs" data-testid="connect-google-cal">
                        <Link2 className="h-3.5 w-3.5 mr-1" /> Connect Google Calendar
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Calendar Grid */}
              <Card className="bg-muted/60 border-border">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-4">
                    <Button variant="ghost" size="sm" onClick={() => changeMonth(-1)} className="h-7 w-7 p-0"><ChevronLeft className="h-4 w-4" /></Button>
                    <h3 className="text-sm font-bold text-foreground" data-testid="cal-month-label">{calMonthLabel}</h3>
                    <Button variant="ghost" size="sm" onClick={() => changeMonth(1)} className="h-7 w-7 p-0"><ChevronRight className="h-4 w-4" /></Button>
                  </div>

                  {/* Weekday headers */}
                  <div className="grid grid-cols-7 gap-1 mb-1">
                    {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(d => (
                      <div key={d} className="text-center text-[9px] font-medium text-muted-foreground py-1">{d}</div>
                    ))}
                  </div>

                  {/* Days grid */}
                  {calLoading ? (
                    <div className="py-8 text-center"><Loader2 className="h-5 w-5 animate-spin text-indigo-400 mx-auto" /></div>
                  ) : (
                    <div className="grid grid-cols-7 gap-1" data-testid="calendar-grid">
                      {/* Leading empty cells for alignment */}
                      {(() => {
                        if (!monthDays.length) return null;
                        const dayMap = { monday: 0, tuesday: 1, wednesday: 2, thursday: 3, friday: 4, saturday: 5, sunday: 6 };
                        const offset = dayMap[monthDays[0]?.day_of_week] || 0;
                        return Array.from({ length: offset }).map((_, i) => <div key={`empty-${i}`} />);
                      })()}
                      {monthDays.map(day => {
                        const dayNum = parseInt(day.date.split('-')[2]);
                        const isPast = new Date(day.date) < new Date(new Date().toISOString().split('T')[0]);
                        const gEventsOnDay = googleEvents.filter(e => e.date === day.date);
                        return (
                          <button key={day.date} onClick={() => !isPast && day.status !== 'closed' && toggleBlockDate(day.date)}
                            disabled={isPast}
                            className={`relative p-1.5 rounded-lg text-center text-xs transition-all ${isPast ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'} ${getStatusColor(day.status)}`}
                            title={`${day.date}: ${day.status}${day.booking_count > 0 ? ` (${day.booking_count} bookings)` : ''}${gEventsOnDay.length > 0 ? ` + ${gEventsOnDay.length} Google events` : ''}`}
                            data-testid={`cal-day-${day.date}`}>
                            <span className="font-semibold">{dayNum}</span>
                            {day.booking_count > 0 && (
                              <span className="absolute top-0.5 right-0.5 w-2 h-2 rounded-full bg-indigo-500" />
                            )}
                            {gEventsOnDay.length > 0 && (
                              <span className="absolute bottom-0.5 right-0.5 w-2 h-2 rounded-full bg-blue-400" />
                            )}
                            {day.status === 'blocked' && (
                              <Ban className="h-2.5 w-2.5 absolute top-0.5 left-0.5 text-muted-foreground" />
                            )}
                          </button>
                        );
                      })}
                    </div>
                  )}

                  {/* Legend */}
                  <div className="flex flex-wrap items-center gap-3 mt-3 pt-3 border-t border-border" data-testid="calendar-legend">
                    <span className="flex items-center gap-1 text-[9px] text-muted-foreground"><span className="w-2.5 h-2.5 rounded bg-emerald-500/30" /> Available</span>
                    <span className="flex items-center gap-1 text-[9px] text-muted-foreground"><span className="w-2.5 h-2.5 rounded bg-amber-500/30" /> Partial</span>
                    <span className="flex items-center gap-1 text-[9px] text-muted-foreground"><span className="w-2.5 h-2.5 rounded bg-red-500/30" /> Full</span>
                    <span className="flex items-center gap-1 text-[9px] text-muted-foreground"><span className="w-2.5 h-2.5 rounded bg-slate-600/40" /> Blocked</span>
                    <span className="flex items-center gap-1 text-[9px] text-muted-foreground"><span className="w-2.5 h-2.5 rounded-full bg-indigo-500" /> Booking</span>
                    {googleCalConnected && <span className="flex items-center gap-1 text-[9px] text-muted-foreground"><span className="w-2.5 h-2.5 rounded-full bg-blue-400" /> Google</span>}
                  </div>
                  <p className="text-[9px] text-muted-foreground mt-1">Click on a future date to block/unblock it</p>
                </CardContent>
              </Card>

              {/* Upcoming Bookings Timeline */}
              {bookings.filter(b => ['confirmed', 'deposit_paid', 'paid'].includes(b.status)).length > 0 && (
                <Card className="bg-muted/60 border-border">
                  <CardContent className="p-4">
                    <h3 className="text-sm font-semibold text-foreground mb-3">Upcoming Confirmed Bookings</h3>
                    <div className="space-y-2">
                      {bookings.filter(b => ['confirmed', 'deposit_paid', 'paid'].includes(b.status)).slice(0, 8).map(b => (
                        <div key={b.booking_id} className="flex items-center gap-3 p-2 rounded-lg bg-muted/50">
                          <div className="text-center min-w-[40px]">
                            <p className="text-[10px] text-indigo-400 font-semibold">{new Date(b.event_date + 'T00:00').toLocaleDateString('en-US', { month: 'short' })}</p>
                            <p className="text-sm font-bold text-foreground">{new Date(b.event_date + 'T00:00').getDate()}</p>
                          </div>
                          <div className="flex-1">
                            <p className="text-xs font-medium text-foreground">{b.customer_name} - {b.space_name}</p>
                            <p className="text-[10px] text-muted-foreground">{b.start_time}-{b.end_time} | {b.guest_count} guests</p>
                          </div>
                          <Badge className="bg-emerald-500/20 text-emerald-400 text-[8px]">{b.status}</Badge>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Google Calendar Events */}
              {googleCalConnected && googleEvents.length > 0 && (
                <Card className="bg-muted/60 border-border">
                  <CardContent className="p-4">
                    <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-1.5">
                      <span className="w-2.5 h-2.5 rounded-full bg-blue-400" /> Google Calendar Events
                    </h3>
                    <div className="space-y-1.5">
                      {googleEvents.slice(0, 10).map(e => (
                        <div key={e.id} className="flex items-center gap-2 p-2 rounded bg-muted/50 text-xs">
                          <span className="text-muted-foreground min-w-[60px]">{e.date}</span>
                          <span className="text-foreground">{e.summary}</span>
                          {e.start_time && <span className="text-muted-foreground ml-auto">{e.start_time}-{e.end_time}</span>}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* === PREFERRED VENDORS TAB === */}
          <TabsContent value="vendors">
            <h2 className="text-sm font-semibold text-foreground mb-1">Preferred Vendors</h2>
            <p className="text-[10px] text-muted-foreground mb-3">Select services from the Supplier Hub to feature on your venue page</p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2" data-testid="vendor-grid">
              {allProducts.map(p => (
                <button key={p.service_id} onClick={() => togglePreferred(p.service_id)}
                  className={`flex items-center gap-3 p-3 rounded-lg border text-left transition-all ${preferredIds.includes(p.service_id) ? 'bg-indigo-500/10 border-indigo-500/30' : 'bg-muted/60 border-border hover:border-border'}`}>
                  {p.image_url ? <img src={p.image_url} alt="" className="w-10 h-10 rounded-lg object-cover flex-shrink-0" /> :
                    <div className="w-10 h-10 rounded-lg bg-muted flex-shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-foreground truncate">{p.name}</p>
                    <p className="text-[10px] text-muted-foreground">${p.base_price} | {p.category}</p>
                  </div>
                  {preferredIds.includes(p.service_id) && <CheckCircle2 className="h-4 w-4 text-indigo-400 flex-shrink-0" />}
                </button>
              ))}
            </div>
          </TabsContent>

          {/* === BOOKINGS TAB === */}
          <TabsContent value="bookings">
            <h2 className="text-sm font-semibold text-foreground mb-3">Booking Requests</h2>
            <div className="space-y-2" data-testid="bookings-list">
              {bookings.map(b => (
                <Card key={b.booking_id} className="bg-muted/60 border-border">
                  <CardContent className="p-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="text-xs font-semibold text-foreground">{b.customer_name}</p>
                          <Badge className={b.status === 'confirmed' ? 'bg-emerald-500/20 text-emerald-400 text-[8px]' : b.status === 'pending' ? 'bg-amber-500/20 text-amber-400 text-[8px]' : b.status === 'cancelled' ? 'bg-red-500/20 text-red-400 text-[8px]' : 'bg-slate-500/20 text-muted-foreground text-[8px]'}>{b.status}</Badge>
                          {b.package_name && <Badge className="bg-indigo-500/20 text-indigo-400 text-[8px]">{b.package_name}</Badge>}
                        </div>
                        <p className="text-[10px] text-muted-foreground mt-0.5">{b.space_name} | {b.event_date} | {b.start_time}-{b.end_time} | {b.guest_count} guests</p>
                        <p className="text-[10px] text-emerald-400 mt-0.5">Total: ${b.total} | Your payout: ${b.venue_payout}</p>
                      </div>
                      {b.status === 'pending' && (
                        <div className="flex gap-1.5">
                          <Button size="sm" onClick={() => updateBooking(b.booking_id, 'confirmed')} className="h-7 bg-emerald-600 hover:bg-emerald-700 text-[10px] px-2">Confirm</Button>
                          <Button size="sm" variant="outline" onClick={() => updateBooking(b.booking_id, 'cancelled')} className="h-7 text-[10px] px-2 text-red-400">Decline</Button>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
              {bookings.length === 0 && <p className="text-sm text-muted-foreground py-8 text-center">No bookings yet</p>}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* ===== SPACE DIALOG ===== */}
      <Dialog open={showSpaceDialog} onOpenChange={setShowSpaceDialog}>
        <DialogContent className="bg-card border-border max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-foreground">{editingSpace ? 'Edit Space' : 'Add Space'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs">Name *</Label><Input value={spaceForm.name} onChange={e => setSpaceForm(p => ({ ...p, name: e.target.value }))} className="bg-muted border-border" placeholder="Grand Ballroom" data-testid="space-name-input" /></div>
            <div><Label className="text-xs">Description</Label><Textarea value={spaceForm.description} onChange={e => setSpaceForm(p => ({ ...p, description: e.target.value }))} className="bg-muted border-border" rows={2} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Min Capacity</Label><Input type="number" value={spaceForm.capacity_min} onChange={e => setSpaceForm(p => ({ ...p, capacity_min: parseInt(e.target.value) || 0 }))} className="bg-muted border-border" /></div>
              <div><Label className="text-xs">Max Capacity</Label><Input type="number" value={spaceForm.capacity_max} onChange={e => setSpaceForm(p => ({ ...p, capacity_max: parseInt(e.target.value) || 0 }))} className="bg-muted border-border" /></div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label className="text-xs">Hourly Rate ($)</Label><Input type="number" value={spaceForm.hourly_rate} onChange={e => setSpaceForm(p => ({ ...p, hourly_rate: parseFloat(e.target.value) || 0 }))} className="bg-muted border-border" /></div>
              <div><Label className="text-xs">Half-Day (4h)</Label><Input type="number" value={spaceForm.half_day_rate} onChange={e => setSpaceForm(p => ({ ...p, half_day_rate: parseFloat(e.target.value) || 0 }))} className="bg-muted border-border" /></div>
              <div><Label className="text-xs">Full-Day (8h)</Label><Input type="number" value={spaceForm.full_day_rate} onChange={e => setSpaceForm(p => ({ ...p, full_day_rate: parseFloat(e.target.value) || 0 }))} className="bg-muted border-border" /></div>
            </div>
            <div><Label className="text-xs">Min Hours</Label><Input type="number" value={spaceForm.minimum_hours} onChange={e => setSpaceForm(p => ({ ...p, minimum_hours: parseInt(e.target.value) || 1 }))} className="bg-muted border-border" /></div>
            <div>
              <Label className="text-xs mb-1 block">Setup Styles</Label>
              <div className="flex flex-wrap gap-1.5">
                {SETUP_STYLES.map(s => (
                  <button key={s} onClick={() => setSpaceForm(p => ({ ...p, setup_styles: p.setup_styles.includes(s) ? p.setup_styles.filter(x => x !== s) : [...p.setup_styles, s] }))}
                    className={`text-[10px] px-2 py-1 rounded-full border transition-all ${spaceForm.setup_styles.includes(s) ? 'bg-indigo-500/20 border-indigo-500/40 text-indigo-400' : 'bg-muted border-border text-muted-foreground'}`}>{s}</button>
                ))}
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" size="sm" onClick={() => setShowSpaceDialog(false)}>Cancel</Button>
              <Button size="sm" onClick={saveSpace} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700" data-testid="save-space-btn">{saving ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Save'}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ===== ADDON DIALOG ===== */}
      <Dialog open={showAddonDialog} onOpenChange={setShowAddonDialog}>
        <DialogContent className="bg-card border-border max-w-md">
          <DialogHeader><DialogTitle className="text-foreground">{editingAddon ? 'Edit Add-on' : 'Add Rental Item'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs">Name *</Label><Input value={addonForm.name} onChange={e => setAddonForm(p => ({ ...p, name: e.target.value }))} className="bg-muted border-border" placeholder="Round Table (seats 8)" data-testid="addon-name-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Category</Label>
                <Select value={addonForm.category} onValueChange={v => setAddonForm(p => ({ ...p, category: v }))}>
                  <SelectTrigger className="bg-muted border-border"><SelectValue /></SelectTrigger>
                  <SelectContent>{ADDON_CATS.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Pricing</Label>
                <Select value={addonForm.pricing_type} onValueChange={v => setAddonForm(p => ({ ...p, pricing_type: v }))}>
                  <SelectTrigger className="bg-muted border-border"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="per_unit">Per Unit</SelectItem>
                    <SelectItem value="flat_fee">Flat Fee</SelectItem>
                    <SelectItem value="per_hour">Per Hour</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Price ($)</Label><Input type="number" value={addonForm.price} onChange={e => setAddonForm(p => ({ ...p, price: parseFloat(e.target.value) || 0 }))} className="bg-muted border-border" /></div>
              <div><Label className="text-xs">Max Qty</Label><Input type="number" value={addonForm.max_quantity} onChange={e => setAddonForm(p => ({ ...p, max_quantity: parseInt(e.target.value) || 1 }))} className="bg-muted border-border" /></div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" size="sm" onClick={() => setShowAddonDialog(false)}>Cancel</Button>
              <Button size="sm" onClick={saveAddon} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700" data-testid="save-addon-btn">{saving ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Save'}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ===== PACKAGE DIALOG ===== */}
      <Dialog open={showPackageDialog} onOpenChange={setShowPackageDialog}>
        <DialogContent className="bg-card border-border max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-foreground flex items-center gap-2"><Sparkles className="h-4 w-4 text-amber-400" /> {editingPackage ? 'Edit Package' : 'Create One-Click Package'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Package Name *</Label><Input value={pkgForm.name} onChange={e => setPkgForm(p => ({ ...p, name: e.target.value }))} className="bg-muted border-border" placeholder="Ultimate Wedding Package" data-testid="package-name-input" /></div>
              <div>
                <Label className="text-xs">Space *</Label>
                <Select value={pkgForm.space_id} onValueChange={v => setPkgForm(p => ({ ...p, space_id: v }))}>
                  <SelectTrigger className="bg-muted border-border"><SelectValue placeholder="Select space" /></SelectTrigger>
                  <SelectContent>{spaces.map(s => <SelectItem key={s.space_id} value={s.space_id}>{s.name} (${s.hourly_rate}/hr)</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div><Label className="text-xs">Description</Label><Textarea value={pkgForm.description} onChange={e => setPkgForm(p => ({ ...p, description: e.target.value }))} className="bg-muted border-border" rows={2} placeholder="Everything you need for the perfect event..." /></div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label className="text-xs">Duration (hrs)</Label><Input type="number" value={pkgForm.duration_hours} onChange={e => setPkgForm(p => ({ ...p, duration_hours: parseInt(e.target.value) || 1 }))} className="bg-muted border-border" /></div>
              <div><Label className="text-xs">Min Guests</Label><Input type="number" value={pkgForm.capacity_min} onChange={e => setPkgForm(p => ({ ...p, capacity_min: parseInt(e.target.value) || 1 }))} className="bg-muted border-border" /></div>
              <div><Label className="text-xs">Max Guests</Label><Input type="number" value={pkgForm.capacity_max} onChange={e => setPkgForm(p => ({ ...p, capacity_max: parseInt(e.target.value) || 1 }))} className="bg-muted border-border" /></div>
            </div>
            <div><Label className="text-xs">Cover Photo URL</Label><Input value={pkgForm.cover_photo} onChange={e => setPkgForm(p => ({ ...p, cover_photo: e.target.value }))} className="bg-muted border-border" placeholder="https://..." /></div>

            {/* Add-on Selection */}
            {addons.length > 0 && (
              <div>
                <Label className="text-xs mb-1 block">Include Add-ons</Label>
                <div className="space-y-1.5 max-h-32 overflow-y-auto">
                  {addons.map(a => {
                    const sel = pkgForm.addon_selections.find(s => s.addon_id === a.addon_id);
                    return (
                      <div key={a.addon_id} className={`flex items-center gap-2 p-2 rounded-lg border transition-all ${sel ? 'bg-indigo-500/10 border-indigo-500/30' : 'bg-muted/50 border-border'}`}>
                        <button onClick={() => togglePkgAddon(a.addon_id)} className="flex-1 text-left">
                          <span className="text-xs text-foreground">{a.name}</span>
                          <span className="text-[10px] text-muted-foreground ml-2">${a.price} {a.pricing_type === 'per_unit' ? '/ea' : ''}</span>
                        </button>
                        {sel && a.pricing_type === 'per_unit' && (
                          <Input type="number" value={sel.quantity} onChange={e => setPkgAddonQty(a.addon_id, e.target.value)} className="w-16 h-7 text-xs bg-muted border-border" min={1} />
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Service Selection from Preferred Vendors */}
            {preferredIds.length > 0 && (
              <div>
                <Label className="text-xs mb-1 block">Include Vendor Services</Label>
                <div className="grid grid-cols-2 gap-1.5 max-h-40 overflow-y-auto">
                  {allProducts.filter(p => preferredIds.includes(p.service_id)).map(p => (
                    <button key={p.service_id} onClick={() => togglePkgService(p.service_id)}
                      className={`flex items-center gap-2 p-2 rounded-lg border text-left transition-all ${pkgForm.service_ids.includes(p.service_id) ? 'bg-indigo-500/10 border-indigo-500/30' : 'bg-muted/50 border-border'}`}>
                      {p.image_url && <img src={p.image_url} alt="" className="w-8 h-8 rounded object-cover flex-shrink-0" />}
                      <div className="min-w-0">
                        <p className="text-[10px] font-medium text-foreground truncate">{p.name}</p>
                        <p className="text-[9px] text-muted-foreground">${p.base_price}</p>
                      </div>
                      {pkgForm.service_ids.includes(p.service_id) && <CheckCircle2 className="h-3 w-3 text-indigo-400 flex-shrink-0 ml-auto" />}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Custom Price ($ 0 = auto-calc)</Label><Input type="number" value={pkgForm.package_price} onChange={e => setPkgForm(p => ({ ...p, package_price: parseFloat(e.target.value) || 0 }))} className="bg-muted border-border" /></div>
              <div><Label className="text-xs">Tags (comma-separated)</Label><Input value={pkgForm.tags} onChange={e => setPkgForm(p => ({ ...p, tags: e.target.value }))} className="bg-muted border-border" placeholder="wedding, corporate" /></div>
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={pkgForm.is_featured} onChange={e => setPkgForm(p => ({ ...p, is_featured: e.target.checked }))} className="rounded bg-muted border-border" />
              <span className="text-xs text-muted-foreground">Featured Package</span>
            </label>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" size="sm" onClick={() => setShowPackageDialog(false)}>Cancel</Button>
              <Button size="sm" onClick={savePackage} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700" data-testid="save-package-btn">{saving ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Save Package'}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
